// Button.tsx
import React from "react";

interface ButtonProps {
  style: "Primary" | "Secondary" | "White" | "Transparent";
  size: "Base" | "Small";
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  textColor?: string;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  style,
  size,
  label,
  onClick,
  icon,
  textColor,
  className = "",
}) => {
  // Base button classes
  const baseClasses = `w-full rounded flex justify-center items-center gap-1.5 ${className}`;

  // Style-specific classes
  const styleClasses = {
    Primary: "bg-[#04262b] hover:bg-[#213f43]",
    Secondary: "bg-[#04262b]/10 hover:bg-[#04262b]/20",
    White: "bg-[#fcfbf8] hover:bg-[#f0f1f1]",
    Transparent: "bg-transparent hover:bg-[#04262b]/10",
  };

  // Default text colors based on style
  const defaultTextColors = {
    Primary: "text-[#fcfbf8]",
    Secondary: "text-[#04262b]",
    White: "text-[#04262b]",
    Transparent: textColor || "",
  };

  // Size-specific classes
  const sizeClasses = {
    Base: "h-11 px-6 py-1.5 text-base leading-normal",
    Small: "h-9 px-4 py-1.5 text-sm leading-tight",
  };

  // Combine all classes for the button
  const buttonClasses = [
    baseClasses,
    styleClasses[style],
    sizeClasses[size],
  ].join(" ");

  // Combine all classes for the label
  const labelClasses = [
    textColor || defaultTextColors[style],
    "font-semibold",
    "font-['Manrope']",
    "tracking-tight",
  ].join(" ");

  return (
    <button className={buttonClasses} onClick={onClick}>
      <span className={labelClasses}>{label}</span>
      {icon && <span className="ml-1">{icon}</span>}
    </button>
  );
};

export default Button;
